import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';

const HomePage = () => {
  const { theme } = useTheme();
  const [user, setUser] = useState(null);

    useEffect(() => {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      setUser(userData);
    }, []);

  return (
    <div 
      className="min-h-screen flex flex-col relative overflow-hidden"
      style={{ backgroundColor: theme.mainPanelBg }}
    >
      {/* Animated Background Overlay */}
      <div className="absolute inset-0 opacity-20 pointer-events-none" style={{ background: `radial-gradient(circle, ${theme.sidebarBg} 20%, transparent 80%)` }}></div>
      
      {/* Header Bar */}
      <header 
        className="relative z-20 border-b border-opacity-10"
        style={{ 
          backgroundColor: theme.mainPanelBg,
          borderColor: theme.inputBorder 
        }}
      >
        <nav className="py-4 px-6">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img
                src="/images/notesdesk.png"
                alt="NoteStruct Logo"
                className="h-10 w-auto object-contain hover:opacity-90 transition-opacity"
              />
            </Link>
            
            {/* Navigation Links */}
            {/* Navigation Links */}
            <div className="hidden md:flex items-center space-x-8">
              <Link
                to="/features"
                className="text-base font-medium hover:opacity-80 transition-opacity"
                style={{ color: theme.mainPanelText }}
              >
                Features
              </Link>
              <Link
                to="/pricing"
                className="text-base font-medium hover:opacity-80 transition-opacity"
                style={{ color: theme.mainPanelText }}
              >
                Pricing
              </Link>
              
              {user?.user_id && user?.plan_status === 'active_pro' && (
                <Link
                  to="/cancel-subscription"
                  className="text-base font-medium hover:opacity-80 transition-opacity"
                  style={{ color: theme.mainPanelText }}
                >
                  Manage Subscription
                </Link>
              )}
              
              <Link
                to="/login"
                className="text-base font-medium hover:opacity-80 transition-opacity"
                style={{ color: theme.mainPanelText }}
              >
                Sign In
              </Link>
              <Link
                to="/signup"
                className="px-6 py-2.5 rounded-lg font-medium shadow-sm hover:shadow-md transition-all"
                style={{ backgroundColor: theme.buttonBg, color: theme.buttonText }}
              >
                Start Free
              </Link>
            </div>
          </div>
        </nav>
      </header>
      
      {/* Hero Section */}
      <div className="flex-1 flex flex-col items-center justify-center text-center p-6 relative z-10">
        <h1 
          className="text-5xl font-extrabold tracking-wide mb-4"
          style={{ color: theme.mainPanelText }}
        >
          Your Ideas, Your Space
        </h1>
        <p 
          className="text-lg max-w-xl mx-auto mb-6"
          style={{ color: theme.mainPanelText }}
        >
          Capture thoughts, organize knowledge, and bring your creativity to life with a seamless note-taking experience.
        </p>
        
        {/* CTA Buttons */}
        <div className="flex gap-6">
          <Link
            to="/signup"
            className="px-6 py-3 rounded-lg font-semibold shadow-md transform hover:scale-105 transition-transform"
            style={{ backgroundColor: theme.buttonBg, color: theme.buttonText }}
          >
            Start Free
          </Link>
          <Link
            to="/login"
            className="px-6 py-3 rounded-lg font-semibold border border-solid hover:bg-opacity-20 transition-all"
            style={{ borderColor: theme.buttonBg, color: theme.mainPanelText }}
          >
            Sign In
          </Link>
        </div>
      </div>
      
      {/* Feature Section */}
      <div 
        className="py-16 relative z-10"
        style={{ backgroundColor: theme.sidebarBg }}
      >
        <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 p-6">
          <FeatureCard 
            title="AI Content Extraction"
            description="Transform dense documents, research papers, and AI conversations into actionable fragments. Our AI intelligently identifies key concepts and insights, saving hours of manual processing."
            theme={theme}
          />
          <FeatureCard 
            title="Visual Fragment Development"
            description="Arrange extracted concepts and raw ideas spatially to discover new connections. Use visual organization as cognitive scaffolding to develop fragments into structured insights."
            theme={theme}
          />
          <FeatureCard 
            title="Synthesis Engine"
            description="Combine fragments into new documents, reports, and deliverables. Whether transforming existing content or creating from scratch, turn scattered thoughts into polished output."
            theme={theme}
          />
          <FeatureCard 
            title="Rich Content Support"
            description="Handle text, code snippets, formatted content, and multimedia fragments seamlessly. Maintain context and formatting throughout your synthesis workflow."
            theme={theme}
          />
        </div>
      </div>

      {/* Pricing Section */}
      <div className="py-16 relative z-10" style={{ backgroundColor: theme.mainPanelBg }}>
        <div className="max-w-4xl mx-auto text-center p-6">
          <h2 
            className="text-3xl font-bold mb-4"
            style={{ color: theme.mainPanelText }}
          >
            Simple, Transparent Pricing
          </h2>
          <p 
            className="text-lg mb-12 opacity-80"
            style={{ color: theme.mainPanelText }}
          >
            Start free and upgrade when you need more power
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-2xl mx-auto">
            <PricingCard 
              title="Free"
              price="$0"
              period="/forever"
              features={[
                "Up to 10 notes",
                "Basic text formatting", 
                "Categories & tags",
                "Email support"
              ]}
              buttonText="Get Started Free"
              buttonLink="/signup"
              theme={theme}
            />
            <PricingCard 
              title="Pro"
              price="$9.99"
              period="/month"
              features={[
                "Unlimited notes",
                "File uploads & images",
                "Advanced sticky notes",
                "Priority support",
                "Export capabilities"
              ]}
              buttonText="Start Free Trial"
              buttonLink="/signup?plan=pro"
              theme={theme}
              featured={true}
            />
          </div>
          
          <p 
            className="text-sm mt-8 opacity-60"
            style={{ color: theme.mainPanelText }}
          >
            All plans include a 14-day free trial. No credit card required.
          </p>
        </div>
      </div>
    </div>
  );
};

// Feature Card Component
const FeatureCard = ({ title, description, theme }) => (
  <div 
    className="p-6 rounded-xl shadow-md hover:shadow-lg transition-all transform hover:-translate-y-1"
    style={{ backgroundColor: theme.mainPanelBg }}
  >
    <h3 
      className="text-2xl font-semibold mb-2"
      style={{ color: theme.mainPanelText }}
    >
      {title}
    </h3>
    <p
      className="text-base"
      style={{ color: theme.mainPanelText }}
    >
      {description}
    </p>
  </div>
);

// Pricing Card Component
const PricingCard = ({ title, price, period, features, buttonText, buttonLink, theme, featured = false }) => (
  <div 
    className={`p-8 rounded-xl shadow-lg transition-all transform hover:-translate-y-1 relative ${featured ? 'border-2' : 'border'}`}
    style={{ 
      backgroundColor: theme.mainPanelBg,
      borderColor: featured ? theme.buttonBg : theme.inputBorder
    }}
  >
    {featured && (
      <div 
        className="absolute -top-3 left-1/2 transform -translate-x-1/2 px-4 py-1 rounded-full text-sm font-semibold"
        style={{ backgroundColor: theme.buttonBg, color: theme.buttonText }}
      >
        Most Popular
      </div>
    )}
    
    <h3 
      className="text-2xl font-bold mb-2"
      style={{ color: theme.mainPanelText }}
    >
      {title}
    </h3>
    
    <div className="mb-6">
      <span 
        className="text-4xl font-bold"
        style={{ color: theme.mainPanelText }}
      >
        {price}
      </span>
      <span 
        className="text-lg opacity-60"
        style={{ color: theme.mainPanelText }}
      >
        {period}
      </span>
    </div>
    
    <ul className="space-y-3 mb-8">
      {features.map((feature, index) => (
        <li 
          key={index}
          className="flex items-center text-sm"
          style={{ color: theme.mainPanelText }}
        >
          <span 
            className="w-2 h-2 rounded-full mr-3"
            style={{ backgroundColor: theme.buttonBg }}
          />
          {feature}
        </li>
      ))}
    </ul>
    
    <Link
      to={buttonLink}
      className={`w-full py-3 px-6 rounded-lg font-semibold text-center block transition-all ${featured ? 'shadow-lg transform hover:scale-105' : 'border border-solid hover:bg-opacity-10'}`}
      style={featured ? 
        { backgroundColor: theme.buttonBg, color: theme.buttonText } :
        { borderColor: theme.buttonBg, color: theme.buttonBg }
      }
    >
      {buttonText}
    </Link>
  </div>
);

export default HomePage;